import React, { useState, useEffect, useRef } from 'react';
import { Button, CircularProgress, Grid, Menu, MenuItem, Typography } from '@mui/material';
import ReactToPdf from 'react-to-pdf';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CompareIcon from 'src/App/assets/compareIcon.svg';
import { KpiPercentileType, ReportTypes, UserReportAccessTypes } from '../../propTypes';
import { COLOR_PRIMARY, exchangeRates, monthNames } from 'src/const';
import {
  calculateHistoricalTrends,
  getDummyMarketURL,
  getSeasonality,
  getSeasonalityBarColor,
  getUserPreferredCurrency,
  handleImageInput,
  parseMessageHistoricalData,
} from 'src/utils/functions';
import RepotPageInputSection from './Components/ReportPageInputSection';
import AutohideSnackbar from 'src/App/Components/Alerts/AutohideSnackbar';

import { reportPageStyles as styles } from './styles';
import ReportPageMapSection from './Components/ReportPageMapSection';
import { COLOR_GREY_400, COLOR_GREY_600, COLOR_PRIMARY_1 } from 'src/colors';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { User } from 'src/interfaces/iuser';
import ShareLinkModal from '../../../../Components/Modals/ShareLinkModal';
import {
  checkIfUserAllowedToEditReport,
  getIfReportTypeDisabled,
  initializeInputEditState,
  initializeMetricsState,
} from '../../helper';
import { useHistory } from 'react-router-dom';
import { callApi } from 'src/core/callApi';
import { getHistoricalData } from 'src/App/Pages/OverviewPage/helper';
import { logAmplitudeEvent } from 'src/utils/analytics';
import {
  financialInputs,
  marketMetrics,
  performanceIndicators,
} from '../../configs/FinancialKPIConfig';
import { CalculatorSection } from './Components';
import RearViewKeyMetrics from 'src/App/HOC/Cards/KeyMetricsCard/RearViewKeyMetrics';
import { SubscriptionModalOrigin, SubscriptionPlan } from 'src/interfaces/idashboard';
import DownloadReportModal from '../../../../Components/Modals/DownloadReportModal';

const ReportPageRightActionSection = ({
  urlPath,
  handleCreateNewReport,
  matches,
  user,
  handleLoginOrPricingModalOpen,
  isUserReportOwner,
  setComingSoonModalOpen,
  pdfRef,
  isPdfDownloadTriggerd,
  setIsPdfDownloadTriggerd,
  reportId,
  setReportComparePageOpen,
  seasonality,
  trend,
  selectedKpiPercentile,
  compsStatus,
  currency,
  grapgDataArray,
}: {
  urlPath: string;
  handleCreateNewReport: () => void;
  matches: boolean;
  user: User;
  handleLoginOrPricingModalOpen: (state: 'open' | 'close', forceOpen?: 'payment' | null) => void;
  isUserReportOwner: boolean;
  setComingSoonModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  pdfRef;
  isPdfDownloadTriggerd: boolean;
  setIsPdfDownloadTriggerd: React.Dispatch<React.SetStateAction<boolean>>;
  reportId: string;
  setReportComparePageOpen: () => void;
  seasonality: number | 'loading' | null;
  trend: number | 'loading' | null;
  selectedKpiPercentile: string;
  compsStatus: 'fetching' | 'success' | 'failed';
  currency: string;
  grapgDataArray: any[];
}) => {
  const [copyLinkModalOpen, setCopyLinkModalOpen] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [pdfDownloadModalOpen, setPdfDownloadModalOpen] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isDownloadingError, setIsDownloadingError] = useState<boolean>(false);
  const [downloadablePdfUrl, setDownloadablePdfUrl] = useState<string | null>(null);
  const isUserHavingCredits: boolean = user.estimatorCredits > 0;
  const isUserSubscribed: boolean = user.subscriptionPlan != 'starter';

  const history = useHistory();

  const downloadPdf = async (settings: {
    logo?: string;
    createdBy: string;
    seasonality: string | null;
    trend: number | null;
    percentile: string;
  }) => {
    setIsDownloading(true);
    setIsPdfDownloadTriggerd(true);
    setIsDownloadingError(false);

    const requestBody: any = {
      report_id: reportId,
      percentile: settings.percentile,
      created_by: settings.createdBy,
      currency: currency,
      logo_url: user.organization_logo_url || null,
    };

    if (settings.seasonality) {
      requestBody.seasonality = settings.seasonality;
    }

    if (settings.trend) {
      requestBody.trend = settings.trend;
    }

    if (settings.logo) {
      requestBody.logo_b64 = settings.logo;
    }

    if (grapgDataArray.length > 0) {
      requestBody.seasonality_graph_data = grapgDataArray;
    }

    try {
      const pdfReportGenereationApi =
        'https://w9f4rz5nx7.execute-api.us-east-2.amazonaws.com/default/generateCalculatorReportPDF';
      const response = await callApi(
        pdfReportGenereationApi,
        { body: requestBody },
        true,
        false,
        '',
        'POST',
        true
      );
      if (response.message && response.message == 'Report generated successfully') {
        const pdfUrl = response.pdf_url;
        window.open(pdfUrl, '_blank');
        setDownloadablePdfUrl(pdfUrl);
      } else {
        setIsDownloadingError(true);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      setIsDownloadingError(true);
    } finally {
      setIsPdfDownloadTriggerd(false);
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Grid
        item
        container
        justifyContent="flex-start"
        sx={{
          ...styles.paper,
          height: 'auto',
          width: 'auto',
          ...(matches && { position: 'fixed', zIndex: 1000, boxShadow: '0 4px 20px -6px #0004' }),
        }}
      >
        <Grid item>
          <Button
            startIcon={<AddRoundedIcon sx={{ fontSize: '18px' }} />}
            sx={{
              ...styles.filterChipStyle(false, COLOR_PRIMARY),
              borderRadius: '8px',
              borderWidth: '1px',
              width: '180px',
              ...(matches ? { fontSize: '14px' } : {}),
            }}
            onClick={() => handleCreateNewReport()}
          >
            New Report
          </Button>
        </Grid>

        <Grid
          item
          id="share-download-menu-anchor"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Button onClick={() => setIsMenuOpen(true)}>
            <MoreVertIcon sx={{ color: COLOR_GREY_600 }} />
          </Button>
          <Menu
            id="lock-menu"
            anchorEl={document.getElementById('share-download-menu-anchor')}
            open={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
            MenuListProps={{ sx: { pr: 5 } }}
          >
            <MenuItem>
              <Button
                startIcon={
                  isPdfDownloadTriggerd ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CloudDownloadOutlinedIcon sx={{ marginRight: '10px' }} />
                  )
                }
                sx={{
                  ...styles.actionSectionButton,
                  backgroundColor: '#fff',
                  color: '#000',
                  justifyContent: 'flex-start',
                  py: 0,
                  '&:hover': {
                    backgroundColor: '#fff',
                  },
                }}
                onClick={() =>
                  isUserSubscribed
                    ? setPdfDownloadModalOpen(true)
                    : handleLoginOrPricingModalOpen('open', null)
                }
              >
                Download{' '}
                {!isUserSubscribed && <LockOutlinedIcon sx={{ ml: 1, color: COLOR_GREY_600 }} />}
              </Button>
            </MenuItem>

            <MenuItem>
              <Button
                startIcon={
                  <ReplyRoundedIcon sx={{ transform: 'scaleX(-1)', marginRight: '10px' }} />
                }
                sx={{
                  ...styles.actionSectionButton,
                  backgroundColor: '#fff',
                  color: '#000',
                  justifyContent: 'flex-start',
                  py: 0,
                  '&:hover': {
                    backgroundColor: '#fff',
                  },
                }}
                onClick={() => {
                  setCopyLinkModalOpen(true);
                  logAmplitudeEvent('report_shared', { source: 'report_page' });
                }}
              >
                Share
              </Button>
            </MenuItem>

            <MenuItem>
              <Button
                startIcon={<AnalyticsIcon sx={{ marginRight: '10px' }} />}
                sx={{
                  ...styles.actionSectionButton,
                  backgroundColor: '#fff',
                  color: '#000',
                  justifyContent: 'flex-start',
                  py: 0,
                  '&:hover': {
                    backgroundColor: '#fff',
                  },
                }}
                onClick={() => history.push('/airbnb-calculator')}
              >
                My Reports
              </Button>
            </MenuItem>

            <MenuItem>
              <Button
                startIcon={
                  <img
                    src={CompareIcon}
                    style={{ width: '20px', height: '20px', marginRight: '10px' }}
                  />
                }
                sx={{
                  ...styles.actionSectionButton,
                  backgroundColor: '#fff',
                  color: '#000',
                  justifyContent: 'flex-start',
                  py: 0,
                  svg: { color: '#000', fill: '#000' },
                  '&:hover': {
                    backgroundColor: '#fff',
                  },
                }}
                onClick={setReportComparePageOpen}
              >
                Compare
              </Button>
            </MenuItem>
          </Menu>
        </Grid>
      </Grid>

      <ShareLinkModal
        open={copyLinkModalOpen}
        setOpen={setCopyLinkModalOpen}
        shareUrl={`https://app.airbtics.com/${urlPath}`}
      />

      <DownloadReportModal
        open={pdfDownloadModalOpen}
        onClose={() => setPdfDownloadModalOpen(false)}
        onDownload={downloadPdf}
        userName={user.userName || ''}
        isDownloading={isDownloading}
        seasonality={seasonality}
        trend={trend}
        percentile={selectedKpiPercentile}
        compsStatus={compsStatus}
        downloadablePdfUrl={downloadablePdfUrl}
        organizationLogo={user.organization_logo_url || null}
      />

      {isDownloadingError && (
        <AutohideSnackbar message="Error downloading report. Please try again." type="error" />
      )}
    </>
  );
};

const ReportPage = ({
  reportData,
  currency,
  handleCreateNewReport,
  handleReport,
  matches,
  user,
  handleLoginOrPricingModalOpen,
  setComingSoonModalOpen,
  dummyOverviewMarket,
  loginCountryCode,
  handleOnSampleClick,
  setReportComparePageOpen,
}: {
  reportData: any;
  currency: string;
  handleCreateNewReport: () => void;
  handleReport: (
    method: any,
    requestBody: null | any,
    id?: string,
    storeID?: boolean,
    hideLoading?: boolean
  ) => Promise<void>;
  matches: boolean;
  user: User;
  handleLoginOrPricingModalOpen: (state: 'open' | 'close', forceOpen?: 'payment' | null) => void;
  setComingSoonModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dummyOverviewMarket: any;
  loginCountryCode: string;
  handleOnSampleClick: (
    reqSubscriptionPlan?: SubscriptionPlan,
    origin?: SubscriptionModalOrigin | null
  ) => void;
  setReportComparePageOpen: () => void;
}) => {
  const isUserReportOwner: boolean = checkIfUserAllowedToEditReport(
    user.email,
    reportData.primary_input?.created_by
  );

  const [selectedKpiPercentile, setSelectedKpiPercentile] = useState<KpiPercentileType>('50');
  const [openedEdits, setOpenedEdits] = useState(new Set());
  const [editCounts, setEditCounts] = useState<{ [key: string]: number }>({});
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);
  const [nearestMarket, setNearestMarket] = useState<any>(null);
  const [nearestMarketURL, setNearestMarketURL] = useState<string | null>(null);
  const [seasonality, setSeasonality] = useState<number | 'loading' | null>('loading');
  const [trend, setTrend] = useState<number | 'loading' | null>('loading');
  const [kpiData, setKpiData] = useState<any>(null);
  const [timePeriod, setTimePeriod] = useState(12);
  const [grapgDataArray, setGraphDataArray] = useState<any[]>([]);
  const [compsStatus, setCompsStatus] = useState<'fetching' | 'success' | 'failed'>('fetching');

  const pdfRef = useRef(null);
  const [isPdfDownloadTriggerd, setIsPdfDownloadTriggerd] = useState<boolean>(false);

  const reportType: ReportTypes = reportData.primary_input.report_type;

  const [metricsState, setMetricsState] = useState({
    marketMetrics: initializeMetricsState(marketMetrics.marketData, reportType),
    incomeMetrics: initializeMetricsState(marketMetrics.income, reportType),
    expensesMetrics: initializeMetricsState(financialInputs.expenses, reportType),
    initialInvestmentMetrics: initializeMetricsState(financialInputs.initialInvestment, reportType),
    cashflowMetrics: initializeMetricsState(performanceIndicators.cashflow, reportType),
    investmentMetrics: initializeMetricsState(performanceIndicators.investmentMetricts, reportType),
  });

  const [inputEditState, setInputEditState] = useState(initializeInputEditState(kpiData));

  const toggleVisibility = (
    category: keyof typeof metricsState,
    metricKey: string,
    show: boolean
  ) => {
    setMetricsState(prevState => {
      const updatedState = { ...prevState };
      updatedState[category][metricKey].visibility = show;
      return updatedState;
    });

    const visibilityConfig = Object.keys(metricsState).reduce((acc, category) => {
      Object.keys(metricsState[category]).forEach(metric => {
        acc[metric] = metricsState[category][metric].visibility;
      });
      return acc;
    }, {});
    localStorage.setItem('kpi_visibility', JSON.stringify(visibilityConfig));
  };

  const handleInputEdit = (key: string, value: number | boolean) => {
    setInputEditState(prevState => ({
      ...prevState,
      [key]: value,
    }));
  };

  const hanldeOnInputApply = () => {
    trackEditPerformed('calculator');
    const calculatorMutableFieldsJsonObj = {
      update_type: 'calculator',
      id: reportData.primary_input.id,
      report_type: reportType,
      ...inputEditState,
      is_loan_taken: inputEditState.is_loan_taken ? 1 : 0,
      wanna_sell: inputEditState.wanna_sell ? 1 : 0,
    };
    handleReport('UPDATE', calculatorMutableFieldsJsonObj);
  };

  const hanldeKpiEdit = (key, value) => {
    trackEditPerformed('calculator');
    const calculatorMutableFieldsJsonObj = {
      update_type: 'kpi',
      id: reportData.primary_input.id,
      report_type: reportType,
      [key]: value,
    };
    handleReport('UPDATE', calculatorMutableFieldsJsonObj);
  };

  useEffect(() => {
    const shouldShowEditedKpi =
      Object.keys(reportData.kpi['edited']).length > 0 && selectedKpiPercentile == '50';

    setKpiData(_ => ({
      ...(shouldShowEditedKpi ? reportData.kpi['edited'] : reportData.kpi[selectedKpiPercentile]),
      ...reportData.fincal_input,
      ...reportData.fincal_input?.loan,
      ...reportData.fincal_input?.annual_operating_expenses,
      ...reportData.fincal_input?.startup_costs,
      ...reportData.fincal_input?.taxes,
      seasonality:
        seasonality == 'loading' ? seasonality : getSeasonalityBarColor(seasonality, true),
      trend,
      ...reportData.kpi,
    }));

    setCompsStatus(reportData.primary_input.comps_status);
  }, [reportData, selectedKpiPercentile, seasonality, trend]);

  useEffect(() => {
    if (compsStatus == 'fetching') {
      let callCount = 0;
      const intervalId = setInterval(() => {
        if (callCount >= 10) {
          setCompsStatus('failed');
          clearInterval(intervalId);
          return;
        }
        handleReport('READ', null, reportData.primary_input.id, false, true);
        callCount++;
      }, 3000);

      return () => clearInterval(intervalId);
    }
    return;
  }, [compsStatus]);

  useEffect(() => {
    setInputEditState(initializeInputEditState(kpiData));
  }, [kpiData]);

  const getNearestMarket = async reportID => {
    const url =
      'https://a1rd9gu3ea.execute-api.us-east-2.amazonaws.com/default/getNearestReportMarket';
    try {
      const response = await callApi(url, { queryParams: { id: reportID } }, false);
      if (response.message) {
        setNearestMarket(response.message);
        const data = response.message;
        const district = data['district'];
        const city = data?.city || '0';
        const country = data?.country ? data?.country.replace(' ', '-') : '0';
        const nearestMarketURL = `${window.location.origin}/airbnb-data/${country}/${city}/${district}`.toLowerCase();
        setNearestMarketURL(nearestMarketURL);
      }
    } catch (e) {
      console.error('Failed to fetch nearest market: ', e);
      setNearestMarket(null);
    }
  };

  useEffect(() => {
    const reportID = reportData.primary_input.id;
    getNearestMarket(reportID);
  }, [reportData]);

  useEffect(() => {
    if (nearestMarket && !nearestMarket.cache_exists) {
      const timer = setTimeout(() => {
        const reportID = reportData.primary_input.id;
        getNearestMarket(reportID);
      }, 5000);
      setSeasonality('loading');
      setTrend('loading');
      return () => clearTimeout(timer);
    }

    if (
      !nearestMarket ||
      !nearestMarket.cached_data ||
      !nearestMarket.cached_data.historical_monthly_performance
    ) {
      return;
    }

    try {
      const cachedMarketData = nearestMarket.cached_data;
      const parsedCachedMarketData = parseMessageHistoricalData(cachedMarketData);

      const [revenueHistoricalData, revenueYoYChange] = getHistoricalData(
        parsedCachedMarketData.historical_monthly_performance,
        'revenue'
      );

      const seasonilityData = getSeasonality(
        revenueHistoricalData,
        'overview',
        selectedKpiPercentile
      );

      const { yearlyYoYChange, nextYearPredcition } = calculateHistoricalTrends(
        revenueHistoricalData,
        'revenue',
        selectedKpiPercentile
      );

      setSeasonality(seasonilityData.seasonality);
      setTrend(nextYearPredcition);
    } catch (e) {
      console.error('Failed to set seasonality and trend from nearest market in report', e);
      setSeasonality(null);
      setTrend(null);
    }
    return;
  }, [nearestMarket, selectedKpiPercentile, reportData]);

  useEffect(() => {
    if (seasonality === 'loading' || trend === 'loading') {
      const maxLoadingTimeout = setTimeout(() => {
        if (seasonality === 'loading') setSeasonality(null);
        if (trend === 'loading') setTrend(null);
      }, 30000);

      return () => clearTimeout(maxLoadingTimeout);
    }
    return;
  }, [seasonality, trend]);

  const isUserSubscribed: boolean = user.subscriptionPlan != 'starter';

  const reportAccessType: UserReportAccessTypes = reportData.primary_input?.user_report_access_type;

  useEffect(() => {
    const savedOnboardingState = localStorage.getItem('user-onboarding-state');
    if (savedOnboardingState) {
      const savedOnboardingStateJSON = JSON.parse(savedOnboardingState);
      setOpenedEdits(new Set(savedOnboardingStateJSON.openedEdits));
      setEditCounts(savedOnboardingStateJSON.editCounts);
      checkOnboardingCompletion();
    }
  }, []);

  useEffect(() => {
    // persisting state across reloads
    const editCountSum = Object.values(editCounts).reduce((acc, value) => acc + value, 0);
    if (openedEdits.size == 0 && editCountSum == 0) {
      return;
    }
    localStorage.setItem(
      'user-onboarding-state',
      JSON.stringify({
        openedEdits: Array.from(openedEdits),
        editCounts,
      })
    );
    checkOnboardingCompletion();
  }, [openedEdits, editCounts]);

  useEffect(() => {
    if (isOnboardingComplete && !isUserSubscribed) {
      const timer = setTimeout(() => {
        handleLoginOrPricingModalOpen('open', 'payment');
      }, 3000);
      return () => clearTimeout(timer);
    }
    return;
  }, [isOnboardingComplete]);

  useEffect(() => {
    setGraphDataArray(getSessionilityGraphData());
  }, [reportData]);

  const getSessionilityGraphData = () => {
    const graphDataObject = {};

    // initialize the graphDataObject with timePeriod
    const monthlyRevenueObject = reportData.kpi?.[selectedKpiPercentile]?.monthly_revenue || {};

    Object.keys(monthlyRevenueObject).forEach(date => {
      graphDataObject[date] = {};
    });

    [25, 50, 75, 90].map((percentile: number) => {
      const monthlyRevenueObject = reportData.kpi[percentile]?.monthly_revenue || {};

      Object.keys(monthlyRevenueObject).forEach((date: any) => {
        graphDataObject[date][percentile] = monthlyRevenueObject[date];
      });
    });

    // const graphData: {
    //   timePeriod: string;
    //   date: string;
    //   Revenue: number;
    //   '0.25': number;
    //   '0.5': number;
    //   '0.75': number;
    //   '0.9': number;
    // }[] = [];

    const monthsOrder = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const latestData = {};

    Object.keys(graphDataObject).forEach(date => {
      const [year, month] = date.split('-');
      const timePeriod = monthsOrder[parseInt(month) - 1];

      if (!latestData[timePeriod] || parseInt(year) > latestData[timePeriod].year) {
        latestData[timePeriod] = {
          year: parseInt(year),
          data: {
            timePeriod: timePeriod,
            date: date,
            Revenue: graphDataObject[date][selectedKpiPercentile],
            '0.25': graphDataObject[date][25],
            '0.5': graphDataObject[date][50],
            '0.75': graphDataObject[date][75],
            '0.9': graphDataObject[date][90],
          },
        };
      }
    });

    const graphData = Object.values(latestData).map((item: any) => item.data);

    graphData.sort((a, b) => monthsOrder.indexOf(a.timePeriod) - monthsOrder.indexOf(b.timePeriod));

    return graphData;
  };

  if (!reportData) {
    return <Typography variant="h4">Loading...</Typography>;
  }

  const getCurrentPropertyOrRentPrice = () => {
    return reportData.fincal_input.property_price
      ? reportData.fincal_input.property_price
      : reportData.fincal_input.monthly_rent
      ? reportData.fincal_input.monthly_rent
      : null;
  };

  const checkOnboardingCompletion = () => {
    const hasOpenedTwoEdits = openedEdits.size >= 2;
    const hasEditedTwice = Object.values(editCounts).reduce((acc, count) => acc + count, 0) >= 2;

    if (hasOpenedTwoEdits || hasEditedTwice) {
      setIsOnboardingComplete(true);
    } else {
      setIsOnboardingComplete(false);
    }
  };

  const trackEditOpened = editType => {
    setOpenedEdits(prevOpenedEdits => {
      const newOpenedEdits = new Set(prevOpenedEdits);
      newOpenedEdits.add(editType);
      return newOpenedEdits;
    });

    if (!editCounts[editType]) {
      setEditCounts(prevEditCounts => ({
        ...prevEditCounts,
        [editType]: 0,
      }));
    }
  };

  const trackEditPerformed = editType => {
    setEditCounts(prevEditCounts => ({
      ...prevEditCounts,
      [editType]: (prevEditCounts[editType] || 0) + 1,
    }));
  };

  // UI Elements

  const ReportPageMapSectionElement = (
    <Grid container item xs={12}>
      <ReportPageMapSection
        reportId={reportData.primary_input.id}
        lat={reportData.primary_input.latitude}
        lng={reportData.primary_input.longitude}
        radius={reportData.primary_input.radius}
        comps={reportData.comps}
        currency={currency}
        handleReport={handleReport}
        isUserReportOwner={isUserReportOwner}
        trackEditOpened={trackEditOpened}
        trackEditPerformed={trackEditPerformed}
        isOnboardingComplete={isOnboardingComplete}
        isUserSubscribed={isUserSubscribed}
        handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
        reportAccessType={reportAccessType}
        kpiData={kpiData}
        compsStatus={compsStatus}
        handleOnSampleClick={handleOnSampleClick}
      />
    </Grid>
  );

  const CreateReportActionButtonElement = (
    <Grid container item xs={12} sx={{ marginBottom: matches ? '60px' : 0 }}>
      <ReportPageRightActionSection
        urlPath={reportData.primary_input.url_path}
        handleCreateNewReport={handleCreateNewReport}
        matches={matches}
        user={user}
        handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
        isUserReportOwner={isUserReportOwner}
        setComingSoonModalOpen={setComingSoonModalOpen}
        pdfRef={pdfRef}
        isPdfDownloadTriggerd={isPdfDownloadTriggerd}
        setIsPdfDownloadTriggerd={setIsPdfDownloadTriggerd}
        reportId={reportData.primary_input.id}
        setReportComparePageOpen={setReportComparePageOpen}
        seasonality={seasonality}
        trend={trend}
        selectedKpiPercentile={selectedKpiPercentile}
        compsStatus={compsStatus}
        currency={currency}
        grapgDataArray={grapgDataArray}
      />
    </Grid>
  );

  const InputSectionElement = (
    <Grid container item xs={12}>
      <RepotPageInputSection
        reportInputs={reportData.primary_input}
        currentPropertyPriceOrRentPrice={getCurrentPropertyOrRentPrice()}
        handleReport={handleReport}
        currency={currency}
        isUserReportOwner={isUserReportOwner}
        trackEditOpened={trackEditOpened}
        trackEditPerformed={trackEditPerformed}
        isOnboardingComplete={isOnboardingComplete}
        isUserSubscribed={isUserSubscribed}
        handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
        loginCountryCode={loginCountryCode}
        selectedKpiPercentile={selectedKpiPercentile}
        setSelectedKpiPercentile={setSelectedKpiPercentile}
      />
    </Grid>
  );

  const MarketMetricsSection = (
    <Grid container item xs={12}>
      {kpiData && (
        <CalculatorSection
          title="Market Metrics"
          subsections={[
            {
              title: 'Market Data',
              key: 'marketMetrics',
              sourceTag: 'Source: Selected n Airbnb Comps',
            },
            { title: 'Income', key: 'incomeMetrics' },
          ]}
          reportKPIData={kpiData}
          currency={currency}
          isUserReportOwner={isUserReportOwner}
          metrics={metricsState}
          toggleVisibility={toggleVisibility}
          hanldeOnInputApply={hanldeOnInputApply}
          hanldeKpiEdit={hanldeKpiEdit}
          trackEditOpened={trackEditOpened}
          trackEditPerformed={trackEditPerformed}
          reportType={reportType}
          inputEditState={inputEditState}
          handleInputEdit={handleInputEdit}
          selectedCompsCount={reportData.comps.reduce((acc: number, comp: any) => {
            if (comp['is_selected']) return acc + 1;
            return acc;
          }, 0)}
          handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
          isUserSubscribed={isUserSubscribed}
        />
      )}
    </Grid>
  );

  const RevenueSeasonalityGraph = (
    <Grid container item xs={12}>
      <Grid
        container
        item
        xs={12}
        justifyContent={'center'}
        sx={{ ...styles.paper, ...styles.reportKPISection }}
      >
        <RearViewKeyMetrics
          title={'Revenue Seasonality'}
          graphData={grapgDataArray}
          color={COLOR_PRIMARY}
          isDisabled={getIfReportTypeDisabled(reportAccessType, 'RevenueLTMGraph')}
          unit={exchangeRates[currency].symbol}
          percentile={Number(selectedKpiPercentile) / 100}
          timePeriod={timePeriod}
          onDisabledOptionClick={() =>
            isUserReportOwner && handleLoginOrPricingModalOpen('open', 'payment')
          }
          isLoading={compsStatus == 'fetching'}
          isFailed={compsStatus == 'failed'}
        />
      </Grid>
    </Grid>
  );

  const PerformanceIndicatorsSection = (
    <Grid container item xs={12}>
      {kpiData && (
        <CalculatorSection
          title="Forecast Your Income"
          subsections={[
            { title: 'Expenses', key: 'expensesMetrics' },
            { title: 'Initial Investment', key: 'initialInvestmentMetrics' },
            { title: 'Cashflow', key: 'cashflowMetrics' },
            { title: 'Investment Metrics', key: 'investmentMetrics' },
          ]}
          reportKPIData={kpiData}
          currency={currency}
          isUserReportOwner={isUserReportOwner}
          metrics={metricsState}
          toggleVisibility={toggleVisibility}
          hanldeOnInputApply={hanldeOnInputApply}
          hanldeKpiEdit={hanldeKpiEdit}
          trackEditOpened={trackEditOpened}
          trackEditPerformed={trackEditPerformed}
          reportType={reportType}
          inputEditState={inputEditState}
          handleInputEdit={handleInputEdit}
          showFutureProjection={true}
          selectedCompsCount={reportData.comps.reduce((acc: number, comp: any) => {
            if (comp['is_selected']) return acc + 1;
            return acc;
          }, 0)}
          handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
          isUserSubscribed={isUserSubscribed}
        />
      )}
    </Grid>
  );

  const DemoMarketStatisticButton = (
    <Grid
      container
      item
      xs={12}
      justifyContent="center"
      sx={matches ? { marginBottom: '60px' } : {}}
    >
      <Grid
        container
        item
        xs={12}
        justifyContent={'center'}
        alignContent={'center'}
        alignItems={'center'}
        sx={{ ...styles.paper, ...styles.reportKPISection, height: '80px', padding: '20px' }}
      >
        <Button
          variant="outlined"
          sx={{ ...styles.actionSectionButton, width: 'auto' }}
          onClick={() => {
            window.open(
              nearestMarketURL ? nearestMarketURL : getDummyMarketURL(dummyOverviewMarket),
              '_self'
            );
          }}
        >
          Go to market statistics
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <>
      {!matches ? (
        // DESKTOP UI
        <Grid
          container
          xs
          sx={{ background: '#F3FAFB', padding: '20px', paddingTop: '40px' }}
          spacing={4}
        >
          {/* Left Section */}
          <Grid container item xs={12} md={6} spacing={2} alignContent="flex-start">
            {CreateReportActionButtonElement}
            {InputSectionElement}
            {MarketMetricsSection}
            {RevenueSeasonalityGraph}
            {DemoMarketStatisticButton}
          </Grid>
          {/* Right Section */}
          <Grid container item xs={12} md={6} spacing={2} alignContent="flex-start">
            {ReportPageMapSectionElement}
            {PerformanceIndicatorsSection}
          </Grid>
        </Grid>
      ) : (
        // MOBILE UI
        <Grid container xs sx={{ background: '#F3FAFB', padding: '20px' }} rowSpacing={2}>
          {CreateReportActionButtonElement}
          {InputSectionElement}
          {ReportPageMapSectionElement}
          {MarketMetricsSection}
          {RevenueSeasonalityGraph}
          {PerformanceIndicatorsSection}
          {DemoMarketStatisticButton}
        </Grid>
      )}

      {isPdfDownloadTriggerd && (
        <Grid
          container
          xs
          sx={{
            background: '#F3FAFB',
            top: '-10000px',
            width: '710px',
            minWidth: '710px',
            paddingRight: '40px',
            paddingBottom: '40px',
          }}
          spacing={4}
          ref={pdfRef}
        >
          {InputSectionElement}
          {MarketMetricsSection}
          <Grid xs={12} container justifyContent={'center'} alignItems={'center'}>
            <Typography variant="body2" sx={{ my: '20px' }}>
              Report generated by <span style={{ color: COLOR_PRIMARY_1 }}>airbtics.com</span> –
              Accurate Airbnb Analytics.
            </Typography>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default React.memo(ReportPage);
